import { Component, inject, OnInit, signal } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { LoginFlow, UpdateLoginFlowWithPasswordMethod } from '@ory/client';
import { TranslocoModule } from '@jsverse/transloco';
import { VxDialogComponent } from '@ui/dialog/dialog.component';
import { VxDialogHeaderComponent } from '@ui/dialog/dialog-header.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { KratosFormV2Component } from '../../components/kratos-form/form.component';

/**
 * Asks the user to confirm their password (or second factor, when one is set
 * up) before a privileged settings change. Opened through `VxDialogService`;
 * closes with `true` once Kratos accepts the login.
 */
@Component({
  selector: 'app-dialog-reauthenticate',
  templateUrl: './reauthenticate.component.html',
  imports: [
    TranslocoModule,
    KratosFormV2Component,
    VxDialogComponent,
    VxDialogHeaderComponent,
    VxSkeletonComponent,
  ],
  host: { class: 'block w-full' },
})
export class ReAuthenticateDialogComponent implements OnInit {
  private dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private kratos = inject(KratosAuthenticationService);

  protected loginFlow = signal<LoginFlow | undefined>(undefined);

  protected useAal2 = signal(false);

  ngOnInit(): void {
    this.kratos.whoami().then(session => {
      // a configured second factor means Kratos expects aal2 here
      const aal2Method = session.authentication_methods?.find(
        method => method.aal === 'aal2'
      );
      this.useAal2.set(!!aal2Method);
      this.kratos
        .createLoginFlow(aal2Method ? 'aal2' : 'aal1')
        .then(flow => this.loginFlow.set(flow));
    });
  }

  protected submitFlow(body: UpdateLoginFlowWithPasswordMethod): void {
    const flow = this.loginFlow();
    if (!flow) {
      return;
    }
    this.kratos
      .completeLoginFlowV2(flow, body.method, { ...body, method: 'password' })
      .then(() => this.dialogRef.close(true))
      .catch(err => {
        // kratos error not always has response data, see: kratos.errors.ts
        if (err?.response?.data) {
          this.loginFlow.set(err.response.data);
        }
      });
  }

  protected close(): void {
    this.dialogRef.close(false);
  }
}
